import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Container, type WorkObject } from "@workcore/schemas";
import { api, get } from "../../api";
import { Breadcrumbs, ErrorBox, PageHeader, Panel } from "../../ui";
import { InlineText, useCanEdit, useDefinition } from "../editor/shared";
import { ObjectTree, treeKey } from "./tree";
import { confirmImpact } from "../../confirm";

/**
 * Template structure (design 3.6, 16.5): the standard phases, deliverables and tasks new
 * projects copy. The one place the console edits objects, because a template is configuration.
 * Only the workflow's admins may write template objects (enforced by the API).
 */
export function StructurePage() {
  const { slug = "", id: idParam = "" } = useParams();
  const id = Number(idParam);
  const q = useQueryClient();
  const container = useQuery({ queryKey: ["ws", slug, "container", id], queryFn: async () => Container.parse(await get(`/v1/ws/${slug}/containers/${id}`)) });
  const c = container.data;
  const def = useDefinition(slug, c?.definitionId ?? 0);
  const canEdit = useCanEdit(slug, c?.definitionId ?? 0);
  const [parent, setParent] = useState<WorkObject>();
  const [name, setName] = useState("");
  const [typeId, setTypeId] = useState("");
  const refresh = () => q.invalidateQueries({ queryKey: treeKey(slug, id) });
  const write = useMutation({
    mutationFn: async ({ method, path, body, impact }: { method: string; path: string; body?: unknown; impact?: string }) => {
      await api(method, path, { ...(body !== undefined ? { body } : {}), dryRun: true });
      if (impact && !(await confirmImpact(impact))) return;
      await api(method, path, body !== undefined ? { body } : {});
    },
    onSettled: refresh,
  });
  const d = def.data;
  // which types may go under the selected node (or at the root), from the containment rules
  const allowed = d ? d.containment.filter((r) => r.parentTypeId === (parent?.typeId ?? null)).map((r) => d.types.find((t) => t.id === r.childTypeId)!).filter(Boolean) : [];
  const add = (e: FormEvent) => {
    e.preventDefault();
    write.mutate(
      { method: "POST", path: `/v1/ws/${slug}/objects`, body: { containerId: id, typeId: Number(typeId), parentId: parent?.id ?? null, name } },
      { onSuccess: () => setName("") },
    );
  };
  const dateEdit = (o: WorkObject, field: "plannedStart" | "plannedEnd") =>
    canEdit ? (
      <input
        type="date"
        aria-label={`${o.name} ${field === "plannedStart" ? "planned start" : "planned end"}`}
        value={o[field] ?? ""}
        onChange={(e) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/objects/${o.id}`, body: { [field]: e.target.value || null } })}
      />
    ) : (
      <span className="small muted">{o[field] ?? "—"}</span>
    );
  return (
    <>
      <ErrorBox error={container.error} />
      {c && !c.isTemplate && <p className="error">This is a live project, not a template. The console does not edit project content.</p>}
      {c && c.isTemplate && d && (
        <>
          <PageHeader
            back={<Breadcrumbs items={[{ label: "Projects", to: `/w/${slug}/projects` }, { label: c.name }, { label: "Template structure" }]} />}
            title={`${c.name}: template structure`}
            description={`Projects made from this template get a copy of this tree, in initial states, with planned dates moved to the project's start. ${canEdit ? "" : "Read only: editing needs workflow admin on " + d.name + "."}`}
          />
          <div className="split">
            <Panel title="Structure">
              <ObjectTree
                slug={slug}
                containerId={id}
                def={d}
                selectedId={parent?.id}
                onSelect={(o) => setParent(parent?.id === o.id ? undefined : o)}
                extra={(o) => (
                  <span className="dates">
                    {dateEdit(o, "plannedStart")} → {dateEdit(o, "plannedEnd")}
                  </span>
                )}
              />
            </Panel>
            <div>
              {canEdit && (
                <Panel title={parent ? `Add under #${parent.id} ${parent.name}` : "Add at the top level"}>
                  {parent && <p className="muted small">Select the node again to add at the top level instead.</p>}
                  {allowed.length === 0 ? (
                    <p className="muted">The workflow allows nothing {parent ? "under this type" : "at the top level"}. Change the containment rules in the workflow editor.</p>
                  ) : (
                    <form autoComplete="off" className="form" onSubmit={add}>
                      <label className="inline-field"><span>Type</span><select aria-label="Type to add" required value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                        <option value="">type…</option>
                        {allowed.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select></label>
                      <label className="inline-field"><span>Name</span><input aria-label="Name to add" required value={name} onChange={(e) => setName(e.target.value)} /></label>
                      <div><button type="submit">Add</button></div>
                    </form>
                  )}
                </Panel>
              )}
              {canEdit && parent && (
                <Panel title={`#${parent.id}`}>
                  <p>
                    Name: <InlineText label="node name" value={parent.name} onSave={(v) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/objects/${parent.id}`, body: { name: v } }, { onSuccess: () => setParent({ ...parent, name: v }) })} />
                  </p>
                  <button
                    className="secondary"
                    onClick={() => write.mutate({ method: "DELETE", path: `/v1/ws/${slug}/objects/${parent.id}`, impact: `Delete '${parent.name}' and everything under it from the template. Projects already created are unaffected; it can be restored.` }, { onSuccess: () => setParent(undefined) })}
                  >
                    Delete…
                  </button>
                </Panel>
              )}
              <ErrorBox error={write.error} />
            </div>
          </div>
        </>
      )}
    </>
  );
}
